import Image from 'next/image';
import { createContext, useContext, useMemo, useState } from 'react';
import TabContainer from '@/components/Modals/Sidebar/components/Tab';

/*
 * WARNING: TypeScript doesn't actually check keys, but we require
 * them to prevent re-rendering issues.
 */
export type Tab = React.ReactElement<{
  key: string;
}>;
export interface SidebarHeaderContextType {
  disabled: boolean;
  setShowLogo: React.Dispatch<React.SetStateAction<boolean>>;
  setTabs: React.Dispatch<React.SetStateAction<Tab[]>>;
  showLogo: boolean;
  tabs: Tab[];
  triggerCooldown: (duration?: number) => void;
}

// biome-ignore lint/suspicious/noEmptyBlockStatements: Default context value placeholder
const noop = () => {};
export const SidebarHeaderContext = createContext<SidebarHeaderContextType>({
  tabs: [],
  setTabs: noop,
  disabled: false,
  triggerCooldown: noop,
  showLogo: false,
  setShowLogo: noop
});
export const useSidebarHeaderContext = () => {
  const context = useContext(SidebarHeaderContext);
  if (!context) {
    throw new Error('useSidebarHeaderContext must be used within a SidebarHeaderProvider');
  }
  return context;
};
export const DefaultTab = () => <TabContainer key="default-header-tab" data-sentry-element="TabContainer" data-sentry-component="DefaultTab" data-sentry-source-file="SidebarHeaderContext.tsx">
    <Image alt="Uppbeat logo" className="h-auto w-[92px]" height={32} src="https://cdn.uppbeat.io/images/logo/logo-marque.svg" width={91} data-sentry-element="Image" data-sentry-source-file="SidebarHeaderContext.tsx" />
  </TabContainer>;
export const SidebarHeaderProvider = ({
  children
}: {
  children: React.ReactNode;
}) => {
  const [tabs, setTabs] = useState<Tab[]>([<DefaultTab />]);
  const [disabled, setDisabled] = useState(false);
  const [showLogo, setShowLogo] = useState(false);
  const triggerCooldown = (duration = 350) => {
    setDisabled(true);
    setTimeout(() => {
      setDisabled(false);
    }, duration);
  };
  const value = useMemo(() => ({
    tabs,
    setTabs,
    disabled,
    triggerCooldown,
    showLogo,
    setShowLogo
  }), [disabled, tabs, showLogo]);
  return <SidebarHeaderContext.Provider value={value} data-sentry-element="SidebarHeaderContext.Provider" data-sentry-component="SidebarHeaderProvider" data-sentry-source-file="SidebarHeaderContext.tsx">
      {children}
    </SidebarHeaderContext.Provider>;
};